import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Deal,
  dealsAPI,
  Pipeline,
  pipelineAPI,
  clientsAPI,
  Client,
  PaymentStatus,
  ShippingStatus,
  User,
  usersAPI,
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  CurrencyDollarIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  XMarkIcon,
  TruckIcon,
  BanknotesIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  UserIcon,
  FunnelIcon as FilterIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import {
  Card,
  CardBody,
  Button,
  Badge,
  Avatar,
  EmptyState,
  Skeleton,
  SkeletonCard,
  Modal,
  Input,
  StatCard,
  Spinner
} from '../ui';
import { formatCurrency, formatDate } from '../../utils/format';

// Helper function to get payment status badge color
const getPaymentStatusColor = (status?: PaymentStatus): 'success' | 'warning' | 'error' | 'primary' | 'neutral' => {
  switch (status) {
    case 'PAID': return 'success';
    case 'PARTIAL': return 'warning';
    case 'PENDING': return 'primary';
    case 'OVERDUE': return 'error';
    case 'REFUNDED': return 'neutral';
    case 'DISPUTED': return 'error';
    case 'CANCELLED': return 'neutral';
    default: return 'neutral';
  }
};

// Helper function to get shipping status badge color
const getShippingStatusColor = (status?: ShippingStatus): 'success' | 'warning' | 'error' | 'primary' | 'neutral' => {
  switch (status) {
    case 'DELIVERED': return 'success';
    case 'IN_TRANSIT': return 'primary';
    case 'OUT_FOR_DELIVERY': return 'primary';
    case 'PICKED_UP': return 'primary';
    case 'LABEL_CREATED': return 'warning';
    case 'NOT_SHIPPED': return 'neutral';
    case 'RETURNED': return 'error';
    case 'EXCEPTION': return 'error';
    default: return 'neutral';
  }
};

// Format status for display
const formatStatus = (status?: string): string => {
  if (!status) return '-';
  return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

interface DealsListProps {
  embedded?: boolean;
}

const DealsList: React.FC<DealsListProps> = ({ embedded = false }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [clients, setClients] = useState<Client[]>([]); // For client filter
  const [salesmen, setSalesmen] = useState<User[]>([]); // For salesman filter

  const [loading, setLoading] = useState(true);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination & Filters State
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10, // Default limit per page
    total: 0,
    totalPages: 1
  });

  const [filters, setFilters] = useState({
    search: '',
    clientId: '',
    pipelineId: '',
    stageId: '',
    paymentStatus: '',
    startDate: '',
    endDate: '',
    userId: '',
  });

  // UI State
  const [showFilters, setShowFilters] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  // Check if we're in admin context
  const isAdminContext = location.pathname.startsWith('/admin');

  // Helper function to generate correct paths
  const getDealPath = (path: string) => {
    return isAdminContext ? `/admin${path}` : path;
  };

  // Initial Data Load (Pipelines & Clients)
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        setFiltersLoading(true);
        const [pipelinesData, clientsData, salesmenData] = await Promise.all([
          pipelineAPI.getAll(),
          clientsAPI.getAll({ limit: 100 }), // Fetch top 100 clients for basic filter
          usersAPI.getSalesmen().catch(() => []), // Fetch salesmen for filter
        ]);
        setPipelines(pipelinesData);
        // Handle clients data format safely
        const clientList = Array.isArray(clientsData) ? clientsData : (clientsData as any).data || [];
        setClients(clientList);
        setSalesmen(salesmenData);
      } catch (err) {
        console.error('Failed to load filters data:', err);
      } finally {
        setFiltersLoading(false);
      }
    };

    fetchMetadata();
  }, []);

  // Fetch Deals when filters or pagination changes
  const fetchDeals = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await dealsAPI.getAllPaginated({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        clientId: filters.clientId || undefined,
        pipelineId: filters.pipelineId || undefined,
        stageId: filters.stageId || undefined,
        paymentStatus: filters.paymentStatus || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        userId: filters.userId || undefined,
      });

      // Handle response safely - support both paginated and legacy array response
      if (Array.isArray(response)) {
        setDeals(response);
        setPagination(prev => ({
          ...prev,
          limit: response.length || 10,
          total: response.length,
          totalPages: 1
        }));
      } else if (response && response.data) {
        setDeals(response.data);
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            ...response.pagination,
          }));
        }
      } else {
        console.error('Unexpected deals response format:', response);
        setDeals([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch deals:', err);
      setError(err.response?.data?.message || 'Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    // Debounce search/filter application could be added here if needed, 
    // but for now we'll trigger on effect
    fetchDeals();
  }, [fetchDeals]);

  // Handlers
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    // Reset to page 1 on filter change
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      clientId: '',
      pipelineId: '',
      stageId: '',
      paymentStatus: '',
      startDate: '',
      endDate: '',
      userId: '',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (id: number) => {
    try {
      await dealsAPI.delete(id);
      setDeals(prev => prev.filter(deal => deal.id !== id));
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete deal');
    }
  };

  // Derived state for stages based on selected pipeline
  const availableStages = filters.pipelineId
    ? pipelines.find(p => p.id === Number(filters.pipelineId))?.stages || []
    : [];

  // Compute salesman performance stats from current filtered deals
  const salesmanStats = React.useMemo(() => {
    if (!filters.userId) return null;
    const totalOrders = pagination.total;
    const totalRevenue = deals.reduce((sum, deal) => sum + Number(deal.amount || 0), 0);
    const avgOrderValue = totalOrders > 0 ? totalRevenue / deals.length : 0;
    const selectedSalesman = salesmen.find(s => s.id === Number(filters.userId));
    const paidOrders = deals.filter(d => d.paymentStatus === 'PAID').length;
    return { totalOrders, totalRevenue, avgOrderValue, selectedSalesman, paidOrders };
  }, [deals, filters.userId, pagination.total, salesmen]);

  // Set default date range to current month if not set (for salesman performance)
  const setCurrentMonthRange = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const today = now.toISOString().split('T')[0];
    setFilters(prev => ({ ...prev, startDate: startOfMonth, endDate: today }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ===== SALESMAN PERFORMANCE BAR (Admin Only) ===== */}
      {isAdminContext && (
        <Card variant="elevated" className="overflow-hidden border-l-4 border-l-primary-500">
          <CardBody className="p-5">
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              {/* Salesman Selector */}
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-1.5">
                  <UserIcon className="h-3.5 w-3.5 inline mr-1" />
                  Salesman
                </label>
                <select
                  value={filters.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                  className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm font-medium py-2.5"
                >
                  <option value="">All Salesmen</option>
                  {salesmen.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.firstName && s.lastName ? `${s.firstName} ${s.lastName}` : s.email}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-1.5">
                  <CalendarIcon className="h-3.5 w-3.5 inline mr-1" />
                  From
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm py-2.5"
                />
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-1.5">
                  <CalendarIcon className="h-3.5 w-3.5 inline mr-1" />
                  To
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm py-2.5"
                />
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 flex-shrink-0">
                <Button variant="outline" size="sm" onClick={setCurrentMonthRange}>
                  This Month
                </Button>
                {(filters.userId || filters.startDate || filters.endDate) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFilters(prev => ({ ...prev, userId: '', startDate: '', endDate: '' }));
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    leftIcon={<XMarkIcon className="h-4 w-4" />}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Performance Summary Cards */}
            {salesmanStats && !loading && (
              <div className="mt-4 pt-4 border-t border-neutral-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <UserIcon className="h-4 w-4 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-neutral-900">
                      {salesmanStats.selectedSalesman?.firstName} {salesmanStats.selectedSalesman?.lastName}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {filters.startDate && filters.endDate
                        ? `${new Date(filters.startDate).toLocaleDateString()} — ${new Date(filters.endDate).toLocaleDateString()}`
                        : 'All time'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3">
                    <p className="text-xs font-medium text-blue-600 uppercase">Total Orders</p>
                    <p className="text-xl font-bold text-blue-900 mt-0.5">{salesmanStats.totalOrders}</p>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-3">
                    <p className="text-xs font-medium text-emerald-600 uppercase">Total Revenue</p>
                    <p className="text-xl font-bold text-emerald-900 mt-0.5">
                      ${salesmanStats.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3">
                    <p className="text-xs font-medium text-purple-600 uppercase">Avg Order</p>
                    <p className="text-xl font-bold text-purple-900 mt-0.5">
                      ${salesmanStats.avgOrderValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3">
                    <p className="text-xs font-medium text-amber-600 uppercase">Paid Orders</p>
                    <p className="text-xl font-bold text-amber-900 mt-0.5">{salesmanStats.paidOrders}</p>
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Stats Overview - Only show if not embedded, not admin (admin has performance bar), and first page */}
      {!embedded && !isAdminContext && !loading && deals.length > 0 && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Deals"
            value={pagination.total}
            icon={<CurrencyDollarIcon />}
            iconBackground="bg-primary-100"
            variant="gradient"
          />
        </div>
      )}

      {/* Actions & Filters Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        {!embedded && (
          <h1 className="text-2xl font-bold text-neutral-900">Deals</h1>
        )}

        <div className="w-full flex flex-col sm:flex-row items-center gap-3 ml-auto">
          {/* Search Input */}
          <div className="w-full sm:max-w-xs relative">
            <Input
              placeholder="Search deals..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="h-5 w-5 text-neutral-400" />}
              className="w-full"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            {!embedded && (
              <Button
                variant="outline"
                leftIcon={<FilterIcon className="h-5 w-5" />}
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'bg-neutral-100' : ''}
              >
                Filters
              </Button>
            )}

            {!embedded && (
              <Link to={getDealPath('/deals/new')} className="w-full sm:w-auto">
                <Button variant="primary" leftIcon={<PlusIcon className="h-5 w-5" />} fullWidth>
                  New Deal
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && !embedded && (
        <Card variant="bordered" className="bg-neutral-50">
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {/* Client Filter */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Client</label>
                <select
                  value={filters.clientId}
                  onChange={(e) => handleFilterChange('clientId', e.target.value)}
                  className="block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Clients</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              {/* Pipeline & Stage Filters */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Pipeline</label>
                <select
                  value={filters.pipelineId}
                  onChange={(e) => {
                    handleFilterChange('pipelineId', e.target.value);
                    handleFilterChange('stageId', ''); // Reset stage when pipeline changes
                  }}
                  className="block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Pipelines</option>
                  {pipelines.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Stage</label>
                <select
                  value={filters.stageId}
                  onChange={(e) => handleFilterChange('stageId', e.target.value)}
                  className="block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  disabled={!filters.pipelineId}
                >
                  <option value="">All Stages</option>
                  {availableStages.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* Salesman Filter */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Salesman</label>
                <select
                  value={filters.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                  className="block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Salesmen</option>
                  {salesmen.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.firstName && s.lastName ? `${s.firstName} ${s.lastName}` : s.email}
                    </option>
                  ))}
                </select>
              </div>

              {/* Payment Status Filter */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Payment Status</label>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                  className="block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="PAID">Paid</option>
                  <option value="PARTIAL">Partial</option>
                  <option value="OVERDUE">Overdue</option>
                  <option value="REFUNDED">Refunded</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>

              {/* Date Ranges */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end mt-4 gap-2">
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear All
              </Button>
              <Button variant="primary" size="sm" onClick={fetchDeals}>
                Apply Filters
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-md flex items-start gap-2">
          <span className="font-medium">Error:</span> {error}
        </div>
      )}

      {/* Content */}
      {loading && deals.length === 0 ? (
        <div className="space-y-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : deals.length > 0 ? (
        <Card variant="elevated" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Deal
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Salesman
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Stage
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Payment
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Shipping
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Problems
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {deals.map((deal) => {
                  const paymentColor = getPaymentStatusColor(deal.paymentStatus);
                  const shippingColor = getShippingStatusColor(deal.shippingStatus);

                  return (
                    <tr key={deal.id} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-primary-900">
                              <Link to={getDealPath(`/deals/${deal.id}`)} className="hover:text-primary-700 hover:underline">
                                {deal.orderNumber || `Deal #${deal.id}`}
                              </Link>
                            </div>
                            <div className="text-xs text-neutral-500 max-w-xs truncate">
                              {deal.description}
                            </div>
                            <div className="text-xs text-neutral-400 mt-0.5">
                              {formatDate(deal.createdAt)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Avatar name={deal.client?.name || '?'} size="sm" className="mr-2" />
                          <div className="text-sm text-neutral-900">{deal.client?.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 text-neutral-400 mr-1.5" />
                          <div className="text-sm text-neutral-700">
                            {deal.user?.firstName && deal.user?.lastName
                              ? `${deal.user.firstName} ${deal.user.lastName}`
                              : deal.user?.email || '-'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-neutral-900">
                          {formatCurrency(Number(deal.amount), deal.currency)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="outline" className="border-primary-200 text-primary-700 bg-primary-50">
                          {deal.stage?.name || '-'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={paymentColor}>
                          {formatStatus(deal.paymentStatus || 'PENDING')}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={shippingColor}>
                          {formatStatus(deal.shippingStatus || 'NOT_SHIPPED')}
                        </Badge>
                        {deal.trackingNumber && (
                          <div className="mt-1 max-w-[10rem] truncate text-xs text-neutral-500">
                            {deal.shippingCarrier ? `${formatStatus(deal.shippingCarrier)} · ` : ''}
                            {deal.trackingUrl ? <a href={deal.trackingUrl} target="_blank" rel="noreferrer" className="font-mono text-primary-600 hover:underline">{deal.trackingNumber}</a> : <span className="font-mono">{deal.trackingNumber}</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(deal.orderIssues?.filter(i => i.status !== 'CLOSED' && i.status !== 'RESOLVED').length || 0) > 0 ? (
                          <Badge variant="error" size="sm">
                            <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                            {deal.orderIssues?.filter(i => i.status !== 'CLOSED' && i.status !== 'RESOLVED').length} Issue(s)
                          </Badge>
                        ) : (
                          <span className="text-neutral-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={getDealPath(`/deals/${deal.id}`)}>
                            <Button variant="ghost" size="sm" className="text-neutral-500 hover:text-primary-600">
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                          </Link>
                          {/* Only show delete if admin or owner? Handled by backend, but UI safety */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-neutral-500 hover:text-error-600"
                            onClick={() => setDeleteConfirm(deal.id)}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-neutral-200 sm:px-6">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-neutral-700">
                    Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50 disabled:bg-neutral-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                    {/* Simplified pagination numbers - just current page for now */}
                    <span className="relative inline-flex items-center px-4 py-2 border border-neutral-300 bg-white text-sm font-medium text-neutral-700">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50 disabled:bg-neutral-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </Card>
      ) : (
        <EmptyState
          type="deals"
          title="No deals found"
          description="Try adjusting your filters or create a new deal."
          action={
            <Link to={getDealPath('/deals/new')}>
              <Button variant="primary" leftIcon={<PlusIcon className="h-5 w-5" />}>
                Create Deal
              </Button>
            </Link>
          }
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Deal"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-neutral-600">
            Are you sure you want to delete this deal? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DealsList;
